/** Import types. */
import type {ReactElement} from "react";
import type {DesignCenterTabProps} from "../Interfaces.tsx";
import type {
    CashReserve,
    CashTranche,
    Portfolio,
    SyntheticTranche,
    TransactionInput,
    WaterfallItem
} from "../../base/ui_general/GeneralInterfaces.tsx";
import {RBSCard, RBSCardVariants} from '../../base/rbs_components/RBSCard';
import {RBSButton, RBSButtonSizes, RBSButtonVariants} from '../../base/rbs_components/RBSButton';
import {Col, Row} from 'react-bootstrap';
import {TransactionDetailModal} from "../modals/TransactionDetailModal.tsx";
import {InputStateControl} from "../../base/utils/InputStateControl.tsx";
import autobind from "autobind-decorator";
import {ApiEndPoints, LabelTextMessageTypes, TransactionTypes, WaterfallItemTypeEnum} from "../../base/Settings.tsx";
import {getRequest_update_transaction} from "../../api/transaction.tsx";
import {BaseComponent, type BaseComponentState} from "../../base/ui_general/BaseComponent.tsx";
import {createSuccessFeedbackMessage} from '../../base/utils/GeneralUtilities';
import {BaseViewStateInitial} from "../../base/ui_general/BaseView.tsx";
import styles from './TabShared.module.css';
import {TabCardHeader} from './components/TabCardHeader';

/**
 * Props for the GeneralTab component.
 * Extends DesignCenterTabProps and adds arrays for portfolios, cash reserves, cash tranches, and waterfall items.
 */
interface GeneralTabProps extends DesignCenterTabProps {
    /** Array of Portfolio objects for the transaction. */
    portfolios: Array<Portfolio>;
    /** Array of CashReserve objects for the transaction. */
    cash_reserves: Array<CashReserve>;
    /** Array of CashTranche objects for the transaction. */
    cash_tranches: Array<CashTranche>;
    /** Array of SyntheticTranche objects for the transaction. */
    synthetic_tranches: Array<SyntheticTranche>;
    /** Array of WaterfallItem objects for the transaction. */
    waterfall_items: Array<WaterfallItem>;
}

/**
 * State for the GeneralTab component.
 * Contains modal visibility and transaction modal validity flag.
 */
interface GeneralTabState extends BaseComponentState {
    /** Controls visibility of the transaction detail modal. */
    showModal: boolean;
    /** Validity flag for the transaction modal input state. */
    transactionModalValid: boolean;
}

/**
 * Component to display general transaction overview and asset/liability table.
 * Handles modal state and input validity updates via InputStateControl.
 */
export class GeneralTab extends BaseComponent<GeneralTabProps, GeneralTabState> {
    /** InputStateControl instance for managing input validity. */
    private readonly inputStateControl: InputStateControl;

    /**
     * Constructor for GeneralTab.
     * Initializes state and sets up InputStateControl validity callback.
     */
    constructor(props: GeneralTabProps) {
        super(props);
        this.state = {
            ...BaseViewStateInitial,
            showModal: false,
            transactionModalValid: true
        };
        this.inputStateControl = new InputStateControl();
        this.inputStateControl.setGlobalValidityUpdateCallback(this.handleInputStateUpdate);
    }

    /**
     * Callback for InputStateControl validity updates.
     * Updates transactionModalValid in state.
     */
    handleInputStateUpdate = (isValid: boolean) => {
        this.setState({transactionModalValid: isValid});
    };

    /**
     * Handles the submit event of the transaction modal.
     * Receives the TransactionInput and can be extended to update state or call a parent callback.
     */
    @autobind
    handleTransactionModalSubmit(transactionInput: TransactionInput) {
        const {transaction, loadData, displayMessage} = this.props;
        if (transaction) {
            const updatedTransaction = {...transaction, ...transactionInput};
            this.processFetchRequest(
                getRequest_update_transaction(updatedTransaction),
                () => {
                    displayMessage(createSuccessFeedbackMessage(LabelTextMessageTypes.TRANSACTION_UPDATED));
                    loadData([ApiEndPoints.TRANSACTION]);
                    this.setState({showModal: false});
                }
            );
        }
    }

    /**
     * Renders the GeneralTab component UI.
     */
    render(): ReactElement {
        /** Display message if no transaction is loaded. */
        if (!this.props.transaction) {
            /**
             * Renders a message when no transaction is loaded.
             * This is a placeholder for the GeneralTab view.
             */
            return (
                <div>
                    <h3>General</h3>
                    <p>No transaction loaded.</p>
                </div>
            );
        }

        /** ---------------- Assets ---------------- */
        /** Map portfolios to asset rows with summed initial notional. */
        const assetPortfolioRows = this.props.portfolios.map((p) => ({
            name: p.name,
            value: p.portfolio_rows.reduce((sum, row) => sum + row.initial_notional, 0),
        }));

        /** ---------------- Liabilities ---------------- */
        /** Map cash tranches to liability rows using associated waterfall items. */
        let liabilityTrancheRows: Array<{ name: string; value: number }> = [];

        if (this.props.transaction.transaction_type === TransactionTypes.CASH) {
            liabilityTrancheRows = this.props.cash_tranches.map((c) => {
                const item = this.props.waterfall_items.find(w => w.id === c.waterfall_item_id && w.type === WaterfallItemTypeEnum.CASH_TRANCHE);
                return {
                    name: item?.name ?? "Unknown Tranche",
                    value: c.initial_nominal,
                };
            });
        }
        if (this.props.transaction.transaction_type === TransactionTypes.SYNTHETIC) {
            liabilityTrancheRows = this.props.synthetic_tranches.map((c) => {
                const item = this.props.waterfall_items.find(w => w.id === c.waterfall_item_id && w.type === WaterfallItemTypeEnum.SYNTHETIC_TRANCHE);
                return {
                    name: item?.name ?? "Unknown Synthetic Tranche",
                    value: c.initial_nominal,
                };
            });
        }

        /** ---------------- Pad rows ---------------- */
        /** Ensure equal number of asset and liability rows (without cash reserves). */
        const maxRows = Math.max(assetPortfolioRows.length, liabilityTrancheRows.length);
        const paddedAssets = [...assetPortfolioRows, ...Array(maxRows - assetPortfolioRows.length).fill({
            name: "",
            value: 0
        })];
        const paddedLiabilities = [...liabilityTrancheRows, ...Array(maxRows - liabilityTrancheRows.length).fill({
            name: "",
            value: 0
        })];

        /** ---------------- Cash Reserves ---------------- */
        /** Add cash reserves to both assets and liabilities. */
        const cashReserveRows = this.props.cash_reserves.map(c => ({
            name: "Cash Reserve",
            value: c.initial_nominal,
        }));

        const finalAssets = [...paddedAssets, ...cashReserveRows];
        const finalLiabilities = [...paddedLiabilities, ...cashReserveRows];

        /** ---------------- Totals ---------------- */
        /** Compute total assets and liabilities. */
        const totalAssets = finalAssets.reduce((sum, a) => sum + a.value, 0);
        const totalLiabilities = finalLiabilities.reduce((sum, l) => sum + l.value, 0);

        /** Determine color for totals: green if equal, red if different. */
        const sumColor = totalAssets === totalLiabilities ? "#004C22" : "#BE5A19";

        /** ---------------- Render ---------------- */
        return (
            <div className={styles.tabContainer}>
                <div className={styles.scrollArea}>
                    {/* Transaction Detail Modal */}
                    <TransactionDetailModal
                        show={this.state.showModal}
                        transaction={this.props.transaction}
                        onClose={() => this.setState({showModal: false})}
                        onSubmit={this.handleTransactionModalSubmit}
                        isValid={this.state.transactionModalValid}
                        inputStateControl={this.inputStateControl}
                        user_language={this.props.user_language}
                    />
                    {/* ---------------- Cards ---------------- */}
                    <div className={styles.cardStack}>
                        {/* ---------------- Card 1: Transaction Info ---------------- */}
                        <RBSCard
                            header={
                                <TabCardHeader
                                    icon={"📝"}
                                    title={<span className={styles.cardHeaderTitle}>Transaction Overview</span>}
                                    actions={
                                        <RBSButton
                                            label="Edit Transaction"
                                            variant={RBSButtonVariants.PRIMARY}
                                            size={RBSButtonSizes.SMALL}
                                            onClick={() => this.setState({showModal: true})}
                                            disabled={!this.props.transaction}
                                        />
                                    }
                                />
                            }
                            header_class_name={styles.cardHeaderPrimary}
                            background={RBSCardVariants.Light}
                            border={RBSCardVariants.Primary}
                            body_class_name={styles.cardBodyPanel}
                            container_class_name={styles.roundedCard}
                            body_text_extra_style={{paddingBottom: "-40px"}}
                        >
                            <Row>
                                <Col md={4} xs={12} className={styles.colTextLeftPad}>
                                    <p><b>Name:</b> {this.props.transaction.name}</p>
                                </Col>
                                <Col md={4} xs={12} className={styles.colTextLeftPad}>
                                    <p><b>Type:</b> {this.props.transaction.transaction_type}</p>
                                </Col>
                                <Col md={4} xs={12} className={styles.colTextLeftPad}>
                                    <p><b>Frequency:</b> {this.props.transaction.payment_frequency}</p>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={4} xs={12} className={styles.colTextLeftPad}>
                                    <p><b>Effective Date:</b> {this.props.transaction.effective_date}</p>
                                </Col>
                                <Col md={4} xs={12} className={styles.colTextLeftPad}>
                                    <p><b>First Payment:</b> {this.props.transaction.first_payment_date}</p>
                                </Col>
                                <Col md={4} xs={12} className={styles.colTextLeftPad}>
                                    <p><b>Maturity Date:</b> {this.props.transaction.maturity_date}</p>
                                </Col>
                            </Row>
                            <Row>
                                <Col xs={12} className={styles.colTextLeftPad}>
                                    <p><b>Description:</b> {this.props.transaction.description}</p>
                                </Col>
                            </Row>
                        </RBSCard>

                        {/* ---------------- Card 2: Assets & Liabilities Table ---------------- */}
                        <RBSCard
                            header={
                                <TabCardHeader
                                    icon={"💼"}
                                    title={<span className={styles.cardHeaderTitle}>Assets & Liabilities</span>}
                                />
                            }
                            header_class_name={styles.cardHeaderPrimary}
                            background={RBSCardVariants.Light}
                            border={RBSCardVariants.Primary}
                            body_class_name={styles.cardBodyPanel}
                            container_class_name={styles.roundedCard}
                            body_text_extra_style={{paddingBottom: "-20px"}}
                        >
                            <Row>
                                <Col md={6} xs={12}>
                                    <table className={styles.dataTable}>
                                        <thead>
                                        <tr>
                                            <th className={`${styles.headerPadLg} ${styles.textRight}`}>Assets</th>
                                            <th className={`${styles.headerPadLg} ${styles.textRight}`}>Value</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {finalAssets.map((a, idx) => (
                                            <tr key={idx}>
                                                <td className={`${styles.cellPadLg} ${styles.textRight} ${styles.summaryRowHeight}`}>{a.name || "\u00A0"}</td>
                                                <td className={`${styles.cellPadLg} ${styles.textRight} ${styles.summaryRowHeight}`}>{a.value > 0 ? a.value.toLocaleString() : "\u00A0"}</td>
                                            </tr>
                                        ))}
                                        <tr className={styles.totalRow}>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`} style={{color: sumColor}}>
                                                Total Assets
                                            </td>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`} style={{color: sumColor}}>
                                                {totalAssets.toLocaleString()}
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </Col>
                                <Col md={6} xs={12}>
                                    <table className={styles.dataTable}>
                                        <thead>
                                        <tr>
                                            <th className={`${styles.headerPadLg} ${styles.textRight}`}>Liabilities</th>
                                            <th className={`${styles.headerPadLg} ${styles.textRight}`}>Value</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {finalLiabilities.map((l, idx) => (
                                            <tr key={idx}>
                                                <td className={`${styles.cellPadLg} ${styles.textRight} ${styles.summaryRowHeight}`}>{l.name || "\u00A0"}</td>
                                                <td className={`${styles.cellPadLg} ${styles.textRight} ${styles.summaryRowHeight}`}>{l.value > 0 ? l.value.toLocaleString() : "\u00A0"}</td>
                                            </tr>
                                        ))}
                                        <tr className={styles.totalRow}>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`} style={{color: sumColor}}>
                                                Total Liabilities
                                            </td>
                                            <td className={`${styles.cellPadLg} ${styles.totalRowCell}`} style={{color: sumColor}}>
                                                {totalLiabilities.toLocaleString()}
                                            </td>
                                        </tr>
                                        </tbody>
                                    </table>
                                </Col>
                            </Row>
                        </RBSCard>
                    </div>
                </div>
            </div>
        );
    }
}
